/**
 * FinancialSummaryCard (Phase 2A) — at-a-glance client financial state.
 *
 * Shows: Outstanding Balance · Open Invoice count · Card on file · Charge Now.
 * Reuses existing invoice data + shared financial semantics (lib/invoiceFinancials).
 *
 * TENANT ISOLATION: invoices are read ONLY from the company-scoped path
 * `companies/{companyId}/invoices` filtered by this client's leadId. There is
 * NO global invoice query. companyId comes from the authenticated CompanyContext.
 */
import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { useCompany } from '../../../../context/CompanyContext';
import { CreditCard, DollarSign, Receipt } from 'lucide-react';
import { getDisplayName } from '../../../../lib/utils';
import {
  sumOutstanding, countOpen, openInvoices, formatMoney, hasActiveCard,
} from '../../../../lib/invoiceFinancials';
import ChargeNowModal from '../../../../components/ChargeNowModal';

const FinancialSummaryCard = ({ client, onViewInvoices }) => {
  const { companyId } = useCompany();
  const [invoices, setInvoices] = useState([]);
  const [chargeOpen, setChargeOpen] = useState(false);

  const leadId = client?.leadId || (!client?.clientId ? client?.id : null);
  const customerName = getDisplayName(client, 'Client');

  useEffect(() => {
    if (!companyId || !leadId) return undefined;
    // Company-scoped query — cannot return another tenant's invoices.
    const q = query(
      collection(db, 'companies', companyId, 'invoices'),
      where('leadId', '==', leadId),
    );
    const unsub = onSnapshot(q, (snap) => {
      setInvoices(snap.docs.map(d => ({ _docId: d.id, ...d.data() })));
    }, (err) => console.error('[FinancialSummaryCard] Firestore error:', err));
    return unsub;
  }, [companyId, leadId]);

  const outstanding = sumOutstanding(invoices);
  const openCount = countOpen(invoices);
  const open = openInvoices(invoices);
  const activeCard = hasActiveCard(client);
  const cardLabel = activeCard && client?.cardBrand && client?.cardLast4
    ? `${client.cardBrand} ••${client.cardLast4}`
    : 'No Card on File';

  const openLabel = openCount === 0
    ? 'No Open Invoices'
    : `${openCount} Open Invoice${openCount === 1 ? '' : 's'}`;

  const canCollect = outstanding > 0 && activeCard;

  const handleCollect = () => {
    // Preserve explicit operator intent: single open invoice → charge it;
    // multiple → send operator to Payment History to pick one (no batch charge).
    if (open.length === 1) setChargeOpen(true);
    else if (onViewInvoices) onViewInvoices();
    else setChargeOpen(true);
  };

  return (
    <div
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4"
      data-testid="financial-summary-card"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Outstanding balance */}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Outstanding Balance</p>
          <p
            className={`text-2xl font-bold tabular-nums ${outstanding > 0 ? 'text-amber-600' : 'text-gray-900'}`}
            data-testid="fin-outstanding-amount"
          >
            {formatMoney(outstanding)}
          </p>
          <button
            type="button"
            onClick={() => onViewInvoices && onViewInvoices()}
            className="text-xs text-gray-500 hover:text-gray-700 mt-0.5 inline-flex items-center gap-1"
            data-testid="fin-open-invoices"
          >
            <Receipt className="h-3 w-3" />
            {openLabel}
          </button>
          {openCount > 0 && (
            <button
              type="button"
              onClick={() => onViewInvoices && onViewInvoices()}
              className="text-xs text-blue-600 hover:text-blue-800 mt-0.5 ml-3 underline"
              data-testid="fin-view-open-invoices"
            >
              View open invoices
            </button>
          )}
        </div>

        {/* Card on file */}
        <div className="sm:text-right">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Payment Method</p>
          <p
            className={`text-sm font-semibold inline-flex items-center gap-1.5 ${activeCard ? 'text-gray-900' : 'text-gray-400'}`}
            data-testid="fin-card-status"
          >
            <CreditCard className="h-3.5 w-3.5" />
            {cardLabel}
          </p>
        </div>

        {/* Charge Now (only when balance > 0 AND active card) */}
        {canCollect && (
          <button
            type="button"
            onClick={handleCollect}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 font-semibold text-sm transition-colors shrink-0"
            data-testid="fin-charge-now-btn"
          >
            <DollarSign className="h-4 w-4" />
            {open.length === 1 ? 'Charge Now' : 'Collect Payment'}
          </button>
        )}
      </div>

      <ChargeNowModal
        isOpen={chargeOpen}
        onClose={() => setChargeOpen(false)}
        companyId={companyId}
        leadId={leadId}
        customerName={customerName}
        invoice={open.length === 1 ? open[0] : null}
        onSuccess={() => setChargeOpen(false)}
      />
    </div>
  );
};

export default FinancialSummaryCard;
